import React, { useCallback, useEffect, useState } from 'react';
import update from 'immutability-helper';
import {
	PlusCircleIcon,
	XCircleIcon,
	XMarkIcon,
} from '@heroicons/react/24/outline';

import { classNames } from '../helpers/functions';
import { TAG_COLOR_OPTIONS } from '../helpers/constants';

import useAsync from '../../hooks/useAsync';
import useModal from '../../hooks/useModal';
import usePrevious from '../../hooks/usePrevious';

import {
	getAssets,
	getLocations,
	getManufacturers,
	getModels,
} from '../../api';
import { getContacts } from '../../api/contacts';
import { getModuleCategories, getModules } from '../../api/modules';
import { getConsumables } from '../../api/consumables';
import { getTags } from '../../api/tags';
import { getTeam } from '../../api/company';

import {
	AttributeRow,
	TemplateBoolean,
	TemplateCreateable,
	TemplateDate,
	TemplateDropdown,
	TemplateField,
	TemplateFile,
	TemplateImage,
	TemplateReactSelect,
	TemplateTextarea,
} from './Attributes_old';

import SelectLocations from '../SelectLocation';

import Badge from '../../components/Badge';
import { AvatarGenerated } from '../../components/Avatar';
import { Card } from '../DragAndDrop/Card';

const Attributes = ({
	field,
	form = {},
	updatedForm,
	onChange,
	setForm,
	attributeId,
	skipOnEditing = [],
	organizationId,
	key,
}) => {
	const [temp, setTemp] = useState(null);
	const [selectedAssets, setSelectedAssets] = useState([]);
	const [sortableChecklist, setSortableChecklist] = useState([]);
	const [checklistInut, setChecklistInput] = useState('');
	const {
		open: addTagOpen,
		setOpen: setAddTagOpen,
		setClose: setAddTagClose,
	} = useModal('addTagModal');

	const { open: addVendorOpen, setOpen: setAddVendorOpen } =
		useModal('addVendorModal');
	const { open: addCategoryOpen, setOpen: setAddCategoryOpen } =
		useModal('addCategoryModal');

	const { open: addLocationOpen, setOpen: setAddLocationOpen } =
		useModal('addLocation');

	const { open: addConsumableOpen, setOpen: setAddConsumableOpen } =
		useModal('addConsumable');

	const { open: addAssetOpen, setOpen: setAddAssetOpen } =
		useModal('assetModalForm');

	const {
		value: vendors,
		execute: onRefreshVendors,
		status: isLoadingVendors,
	} = useAsync(getContacts, {
		immediate: false,
	});

	const {
		value: team,
		execute: onRefreshTeam,
		status: isLoadingTeam,
	} = useAsync(getTeam, {
		immediate: false,
	});

	const {
		value: categories,
		execute: onRefreshCategories,
		status: isLoadingCategories,
	} = useAsync(getModuleCategories, {
		immediate: false,
	});

	const {
		value: locations,
		execute: onRefreshLocations,
		status: isLoadingLocations,
	} = useAsync(getLocations, {
		immediate: false,
	});

	const {
		value: modules,
		execute: onRefreshModules,
		status: isLoadingModules,
	} = useAsync(getModules, {
		immediate: false,
	});

	const {
		value: consumables,
		execute: onRefreshConsumables,
		status: isLoadingConsumables,
	} = useAsync(getConsumables, {
		immediate: false,
	});

	const {
		value: items,
		execute: onRefreshItems,
		status: isLoadingItems,
	} = useAsync(getAssets, { immediate: false });

	const {
		value: manufacturers,
		execute: onRefreshManufacturers,
		status: isLoadingManufacturers,
	} = useAsync(getManufacturers, { immediate: false });

	const {
		value: models,
		execute: onRefreshModels,
		status: isLoadingModels,
	} = useAsync(getModels, { immediate: false });

	const {
		value: tags,
		execute: onRefreshTags,
		status: isLoadingTags,
	} = useAsync(getTags, { immediate: false });

	useEffect(() => {
		if (field?.type === 'vendor') {
			onRefreshVendors();
		} else if (field?.type === 'team') {
			onRefreshTeam({ active: true });
		} else if (field?.type === 'category') {
			const defaultModuleKey = field?.options?.module_key;

			if (defaultModuleKey) {
				onRefreshModules(null, (localModules) => {
					onRefreshCategories({
						id:
							localModules?.rows?.find((o) => o.key === defaultModuleKey)?.id ||
							field?.moduleId ||
							field?.options?.module_key,
						...(organizationId ? { organizationId } : {}),
					});
				});
			} else {
				onRefreshCategories({
					id: field?.moduleId,
					...(organizationId ? { organizationId } : {}),
				});
			}
		} else if (field?.type === 'location') {
			onRefreshLocations({ ...(organizationId ? { organizationId } : {}) });
		} else if (field?.type === 'module') {
			onRefreshModules();
		} else if (field?.type === 'consumable') {
			onRefreshConsumables();
		} else if (field?.type === 'manufacturer') {
			onRefreshManufacturers({ ...(organizationId ? { organizationId } : {}) });
		} else if (field?.type === 'assets' || field?.type === 'assetsList') {
			onRefreshItems({
				pageSize: null,
				...(organizationId ? { organizationId } : {}),
			});
		} else if (field?.type === 'tag') {
			onRefreshTags({ ...(organizationId ? { organizationId } : {}) });
		}
	}, [field?.type]);

	useEffect(() => {
		if (field?.type === 'model') {
			onRefreshModels({
				manufacturerId: form['manufacturer']?.value,
				...(organizationId ? { organizationId } : {}),
			});
		}
	}, [form['manufacturer']?.value]);

	const prevAddTagOpen = usePrevious(addTagOpen);
	const prevAddVendorOpen = usePrevious(addVendorOpen);
	const prevAddCategoryOpen = usePrevious(addCategoryOpen);
	const prevAddConsumableOpen = usePrevious(addConsumableOpen);
	const prevAddLocationOpen = usePrevious(addLocationOpen);
	const prevAddAssetOpen = usePrevious(addAssetOpen);

	useEffect(() => {
		if (field?.type === 'vendor' && !!prevAddVendorOpen && !addVendorOpen) {
			onRefreshVendors();
		}
	}, [addVendorOpen]);

	useEffect(() => {
		if (
			(field?.type === 'assets' || field?.type === 'assetsList') &&
			!!prevAddAssetOpen &&
			!addAssetOpen
		) {
			onRefreshItems({
				pageSize: null,
				...(organizationId ? { organizationId } : {}),
			});
		}
	}, [addAssetOpen]);

	useEffect(() => {
		if (field?.type === 'tag' && !!prevAddTagOpen && !addTagOpen) {
			onRefreshTags({ ...(organizationId ? { organizationId } : {}) });
		}
	}, [addTagOpen]);

	useEffect(() => {
		if (
			field?.type === 'category' &&
			!!prevAddCategoryOpen &&
			!addCategoryOpen
		) {
			const defaultModuleKey = field?.options?.module_key;

			if (defaultModuleKey) {
				onRefreshModules(null, (localModules) => {
					onRefreshCategories({
						id:
							localModules?.rows?.find((o) => o.key === defaultModuleKey)?.id ||
							field?.moduleId,
						...(organizationId ? { organizationId } : {}),
					});
				});
			} else {
				onRefreshCategories({
					id: field?.moduleId,
					...(organizationId ? { organizationId } : {}),
				});
			}
		}
	}, [addCategoryOpen]);

	useEffect(() => {
		if (
			field?.type === 'consumable' &&
			!!prevAddConsumableOpen &&
			!addConsumableOpen
		) {
			onRefreshConsumables();
		}
	}, [addConsumableOpen]);

	useEffect(() => {
		if (
			field?.type === 'location' &&
			!!prevAddLocationOpen &&
			!addLocationOpen
		) {
			onRefreshLocations({ ...(organizationId ? { organizationId } : {}) });
		}
	}, [addLocationOpen]);

	useEffect(() => {
		if (form['tags']?.value) {
			setTemp(
				form['tags']?.value?.map((tag) => ({
					label: tag?.name || tag?.label,
					value: tag?.id || tag?.value,
					color: tag?.color,
				})),
			);
		}
	}, [form['tags']?.value]);

	const initialAssets = usePrevious(form?.assets?.value);
	useEffect(() => {
		if (form?.assets?.value && !initialAssets) {
			setSelectedAssets(form?.assets?.value);
		}
	}, [form?.assets?.value]);

	useEffect(() => {}, [sortableChecklist?.length]);

	const moveCard = useCallback((dragIndex, hoverIndex) => {
		setSortableChecklist((prevCards) =>
			update(prevCards, {
				$splice: [
					[dragIndex, 1],
					[hoverIndex, 0, prevCards[dragIndex]],
				],
			}),
		);
	}, []);

	const renderCard = useCallback((card, index, sortableChecklist) => {
		return (
			<Card
				key={card.id}
				index={index}
				id={card.id}
				text={card.text}
				moveCard={moveCard}
				removeCard={(indexToRemove) => {
					const updatedList = [
						...sortableChecklist.filter(
							(item, index) => index !== indexToRemove,
						),
					];
					setSortableChecklist(updatedList);

					//onChange('checklist', updatedList);
				}}
			/>
		);
	}, []);

	if (!field) {
		return 'Failed to load field';
	}

	if (field?.type === 'text') {
		return (
			<TemplateField
				name={field?.label}
				span={field?.span ? `sm:col-span-${field?.span}` : undefined}
				dataIndex={field?.key}
				required={field?.isRequired}
				onChange={(index, value) => onChange(index, value)}
				value={form[field?.key]?.value}
				optional={field?.optional}
				disabled={field?.disabled}
			/>
		);
	}

	if (field?.type === 'date') {
		return (
			<div
				className={classNames(
					field?.span ? `sm:col-span-${field?.span}` : 'sm:col-span-2',
					' flex h-fit',
				)}
			>
				<div className="flex-1">
					<TemplateDate
						name={field?.label}
						placeholder={field?.placeholder}
						span={field?.span ? `sm:col-span-${field?.span}` : undefined}
						dataIndex={field?.key}
						required={field?.isRequired}
						onChange={(index, value) => onChange(index, value)}
						value={form[field?.key]?.value}
						optional={field?.optional}
						disabled={field?.disabled}
					/>
				</div>
				{form[field?.key] &&
				(field?.isClearable || field?.options?.isClearable) ? (
					<div className="flex-shrink flex items-center pt-7 ml-3">
						<XCircleIcon
							className="w-6 h-6 cursor-pointer text-gray-500"
							onClick={() => onChange(field?.key, null)}
						/>
					</div>
				) : null}
			</div>
		);
	}

	if (field?.type === 'numeric') {
		return (
			<TemplateField
				name={field?.label}
				span={field?.span ? `sm:col-span-${field?.span}` : undefined}
				dataIndex={field?.key}
				required={field?.isRequired}
				onChange={(index, value) => onChange(index, value)}
				value={parseInt(
					form[field?.key]?.value !== null
						? form[field?.key]?.value
						: field?.defaultValue,
				)}
				type="number"
				pattern="^-?[0-9]\d*\.?\d*$"
				disabled={field?.disabled}
			/>
		);
	}

	if (field?.type === 'boolean') {
		return (
			<TemplateBoolean
				span={field?.span ? `sm:col-span-${field?.span}` : undefined}
				name={field?.label}
				dataIndex={field?.key}
				required={field?.isRequired}
				onChange={(index, value) => onChange(index, value)}
				value={form[field?.key]?.value}
				defaultValue={field?.defaultValue}
				disabled={field?.disabled}
			/>
		);
	}

	if (field?.type === 'image') {
		return (
			<TemplateImage
				name={field?.label}
				dataIndex={field?.key}
				required={field?.isRequired}
				onChange={(index, value) => {
					onChange('images', value);
				}}
				maxImages={field?.maxImages}
				disabled={field?.disabled}
			/>
		);
	}

	if (field?.type === 'file') {
		return (
			<TemplateFile
				name={field?.label}
				span={field?.span ? `sm:col-span-${field?.span}` : undefined}
				dataIndex={field?.key}
				required={field?.isRequired}
				onChange={(index, value) => {
					onChange(field?.key, value);
				}}
				maxImages={field?.maxImages}
				disabled={field?.disabled}
				value={form[field?.key]?.value}
			/>
		);
	}

	if (field?.type === 'textarea') {
		return (
			<TemplateTextarea
				span={field?.span ? `sm:col-span-${field?.span}` : undefined}
				placeholder={field?.options?.placeholder || field?.placeholder}
				name={field?.label}
				dataIndex={field?.key}
				required={field?.isRequired}
				onChange={(index, value) => onChange(index, value)}
				value={form[field?.key]?.value}
				disabled={field?.disabled}
			/>
		);
	}

	if (field?.type === 'time') {
		const conditionalKeys = field?.skipConditions
			? []
			: Object.keys(field?.options?.conditional || {});
		if (conditionalKeys?.length) {
			let hide = true;

			conditionalKeys?.map((key) => {
				const actualValue = form[key]?.value;
				const expectedValue = field?.options?.conditional[key];

				if (expectedValue === 'undefined') {
					if (actualValue === 'undefined' || !actualValue) {
						hide = false;
					}
				} else if (actualValue === expectedValue) {
					hide = false;
				}
				return;
			});

			if (hide) {
				return null;
			}
		}

		return (
			<TemplateDropdown
				span={field?.span ? `sm:col-span-${field?.span}` : undefined}
				name={field?.label}
				dataIndex={field?.key}
				className={field?.className}
				required={field?.isRequired}
				onChange={(index, value) => onChange(index, value)}
				value={form[field?.key]?.value || field?.defaultValue}
				options={[
					{ label: '6:00AM', value: '6:0' },
					{ label: '6:30AM', value: '6:30' },

					{ label: '7:00AM', value: '7:0' },
					{ label: '7:30AM', value: '7:30' },

					{ label: '8:00AM', value: '8:0' },
					{ label: '8:30AM', value: '8:30' },

					{ label: '9:00AM', value: '9:0' },
					{ label: '9:30AM', value: '9:30' },

					{ label: '10:00AM', value: '10:0' },
					{ label: '10:30AM', value: '10:30' },

					{ label: '11:00AM', value: '11:0' },
					{ label: '11:30AM', value: '11:30' },

					{ label: 'Noon', value: '12:0' },
					{ label: '12:30PM', value: '12:30' },

					{ label: '1:00PM', value: '13:0' },
					{ label: '1:30PM', value: '13:30' },

					{ label: '2:00PM', value: '14:0' },
					{ label: '2:30PM', value: '14:30' },

					{ label: '3:00PM', value: '15:0' },
					{ label: '3:30PM', value: '15:30' },

					{ label: '4:00PM', value: '16:0' },
					{ label: '4:30PM', value: '16:30' },

					{ label: '5:00PM', value: '17:0' },
					{ label: '5:30PM', value: '17:30' },

					{ label: '6:00PM', value: '18:0' },
				]}
			/>
		);
	}

	if (field?.type === 'options') {
		const options = field?.options?.options || [];

		const conditionalKeys = Object.keys(field?.options?.conditional || {});
		if (conditionalKeys?.length) {
			let hide = true;

			conditionalKeys?.map((key) => {
				const actualValue = form[key]?.value;
				const expectedValue = field?.options?.conditional[key];

				if (expectedValue === 'undefined') {
					if (actualValue === 'undefined' || !actualValue) {
						hide = false;
					}
				} else if (expectedValue === 'notNull') {
					if (
						actualValue !== null &&
						actualValue !== 'null' &&
						typeof actualValue !== 'undefined' &&
						actualValue !== 'undefined'
					) {
						hide = false;
					}
				} else if (actualValue === expectedValue) {
					hide = false;
				}
				return;
			});

			if (hide) {
				return null;
			}
		}

		return (
			<div
				className={classNames(
					field?.span ? `sm:col-span-${field?.span}` : 'sm:col-span-2',
					' flex h-fit',
				)}
			>
				<div className="flex-1">
					<TemplateDropdown
						span={field?.span ? `sm:col-span-${field?.span}` : undefined}
						name={field?.label}
						dataIndex={field?.key}
						className={field?.className}
						required={field?.isRequired}
						onChange={(index, value) => onChange(index, value)}
						value={form[field?.key]?.value || field?.defaultValue}
						disabled={field?.disabled}
						options={options}
					/>
				</div>
				{form[field?.key] &&
				(field?.isClearable || field?.options?.isClearable) ? (
					<div className="flex-shrink flex items-center pt-7 ml-3">
						<XCircleIcon
							className="w-6 h-6 cursor-pointer text-gray-500"
							onClick={() => onChange(field?.key, null)}
						/>
					</div>
				) : null}
			</div>
		);
	}

	if (field?.type === 'vendor') {
		const vendorOptions = vendors?.rows
			.sort((a, b) => {
				const Aname = a.companyName || a.firstName || a.lastName;
				const Bname = b.companyName || b.firstName || b.lastName;

				return Aname.localeCompare(Bname);
			})
			.map((vendor) => ({
				label:
					vendor?.companyName ||
					(vendor?.firstName && vendor?.lastName
						? `${vendor?.firstName} ${vendor?.lastName}`
						: vendor?.firstName || vendor?.lastName || 'N/A'),
				value: vendor.id,
			}));

		const selectedVendor =
			vendorOptions?.find(
				(o) =>
					o.value === form[field?.key]?.value ||
					o.value === form[field?.key]?.value?.value,
			) || '';

		return (
			<div
				className={classNames(
					field?.span ? `sm:col-span-${field?.span}` : 'sm:col-span-2',
					' flex h-fit',
				)}
			>
				<div className="flex-1">
					<TemplateReactSelect
						name={field?.label}
						dataIndex={field?.key}
						required={field?.isRequired}
						options={vendorOptions}
						value={selectedVendor}
						onChange={(index, vendor) => onChange(index, vendor)}
						disabled={isLoadingVendors === 'pending' || field?.disabled}
						placeholder={field?.options?.placeholder}
					/>
				</div>
				{form[field?.key]?.value ? (
					<div className="flex-shrink flex items-center pt-7 ml-3">
						<XCircleIcon
							className="w-6 h-6 cursor-pointer text-gray-500"
							onClick={() => onChange(field?.key, null)}
						/>
					</div>
				) : (
					<div className="flex-shrink flex items-center pt-7 ml-3">
						<PlusCircleIcon
							className="w-6 h-6 cursor-pointer text-gray-500"
							onClick={() =>
								setAddVendorOpen({
									onSubmit: (vendor) => onChange(field?.key, vendor),
								})
							}
						/>
					</div>
				)}
			</div>
		);
	}

	if (field?.type === 'team') {
		const teamOptions = team?.rows
			.sort((a, b) => {
				const Aname = a.first_name || a.last_name || a.email;
				const Bname = b.first_name || b.last_name || b.email;

				return Aname.localeCompare(Bname);
			})
			.map((vendor) => ({
				label:
					vendor?.first_name && vendor?.last_name
						? `${vendor?.first_name} ${vendor?.last_name}`
						: vendor?.first_name || vendor?.last_name || 'N/A',
				value: vendor.id,
			}));

		const selectedTeamMember =
			teamOptions?.find(
				(o) =>
					o.value === form[field?.key]?.value ||
					o.value === form[field?.key]?.value?.value,
			) || '';

		return (
			<div
				className={classNames(
					field?.span ? `sm:col-span-${field?.span}` : 'sm:col-span-2',
					' flex h-fit',
				)}
			>
				<div className="flex-1">
					<TemplateReactSelect
						name={field?.label}
						dataIndex={field?.key}
						required={field?.isRequired}
						options={teamOptions}
						value={selectedTeamMember}
						onChange={(index, vendor) => onChange(index, vendor)}
						disabled={isLoadingTeam === 'pending' || field?.disabled}
					/>
				</div>
				{form[field?.key] ? (
					<div className="flex-shrink flex items-center pt-7 ml-3">
						<XCircleIcon
							className="w-6 h-6 cursor-pointer text-gray-500"
							onClick={() => onChange(field?.key, null)}
						/>
					</div>
				) : null}
			</div>
		);
	}

	if (field?.type === 'module') {
		return (
			<>
				<div className="col-span-2 flex h-fit">
					<div className="flex-1">
						<TemplateReactSelect
							name={field?.label}
							dataIndex={field?.key}
							required={field?.isRequired}
							options={modules?.rows
								?.sort((a, b) => a.name?.localeCompare(b.name))
								.map((module) => ({
									label: module?.name,
									value: module.id,
									fields: module?.attribute_fields,
								}))}
							value={form[field?.key]?.value || ''}
							onChange={(index, vendor) => onChange(index, vendor)}
							disabled={isLoadingModules === 'pending' || field?.disabled}
						/>
					</div>
					{form[field?.key] ? (
						<div className="flex-shrink flex items-center pt-7 ml-3">
							<XCircleIcon
								className="w-6 h-6 cursor-pointer text-gray-500"
								onClick={() => onChange(field?.key, null)}
							/>
						</div>
					) : null}
				</div>
			</>
		);
	}

	if (field?.type === 'consumable') {
		return (
			<>
				<div className="col-span-2 flex h-fit">
					<div className="flex-1">
						<TemplateReactSelect
							name={field?.label}
							dataIndex={field?.key}
							options={consumables?.rows
								.sort((a, b) => a.name?.localeCompare(b.name))
								.map((consumable) => ({
									label: consumable?.name,
									value: consumable.id,
									consumable,
								}))}
							value={form[field?.key]?.value || ''}
							onChange={(index, vendor) => {
								onChange(index, vendor);
							}}
							disabled={isLoadingConsumables === 'pending' || field?.disabled}
						/>
					</div>
					{form[field?.key] ? (
						<div className="flex-shrink flex items-center pt-7 ml-3">
							<XCircleIcon
								className="w-6 h-6 cursor-pointer text-gray-500"
								onClick={() => onChange(field?.key, null)}
							/>
						</div>
					) : field?.allowNew ? (
						<div className="flex-shrink flex items-center pt-7 ml-3">
							<PlusCircleIcon
								className="w-6 h-6 cursor-pointer text-gray-500"
								onClick={() =>
									setAddConsumableOpen({
										title: field?.modal?.title || undefined,
										moduleId: field?.moduleId,
										onSubmit: (consumable) => onChange(field?.key, consumable),
									})
								}
							/>
						</div>
					) : null}
				</div>
			</>
		);
	}

	if (field?.type === 'category') {
		const categoryOptions = categories
			?.sort((a, b) => a.name?.localeCompare(b.name))
			.map((category) => ({
				label: category?.name,
				value: category.id,
				fields: category?.attribute_fields,
			}));

		const selectedCategory = categoryOptions?.find(
			(o) =>
				o.value === form[field?.key]?.value ||
				o.value === form[field?.key]?.value?.value,
		);

		const isChecklist = form[field?.key]?.value?.label === 'Checklist';

		const defaultModuleKey = field?.options?.module_key;

		const defaultModuleId =
			modules?.rows?.find((o) => o.key === defaultModuleKey)?.id || null;

		return (
			<>
				<div
					className={classNames(
						isChecklist
							? 'sm:col-span-2'
							: field?.span
								? `sm:col-span-${field?.span}`
								: undefined,
						'flex h-fit',
					)}
				>
					<div className="flex-1">
						<TemplateReactSelect
							name={field?.label}
							dataIndex={field?.key}
							required={field?.isRequired}
							options={categoryOptions}
							value={selectedCategory || ''}
							onChange={(index, vendor) => {
								onChange(index, vendor);
							}}
							disabled={isLoadingCategories === 'pending' || field?.disabled}
						/>
					</div>
					{form[field?.key]?.value ? (
						<div className="flex-shrink flex items-center pt-7 ml-3">
							<XCircleIcon
								className="w-6 h-6 cursor-pointer text-gray-500"
								onClick={() => {
									onChange(field?.key, null);
									setChecklistInput('');
									setSortableChecklist([]);
								}}
							/>
						</div>
					) : (
						<div className="flex-shrink flex items-center pt-7 ml-3">
							<PlusCircleIcon
								className="w-6 h-6 cursor-pointer text-gray-500"
								onClick={() =>
									setAddCategoryOpen({
										moduleId: defaultModuleId || field?.moduleId,
										onSubmit: (category) => onChange(field?.key, category),
										...(organizationId ? { organizationId } : {}),
									})
								}
							/>
						</div>
					)}
				</div>

				{isChecklist && (
					<div className="sm:col-span-2 px-10  ">
						<div>
							{sortableChecklist.map((card, i) =>
								renderCard(card, i, sortableChecklist),
							)}
						</div>

						<div>
							<input
								className="border w-full text-xs px-4 py-2 rounded-lg my-2 focus:outline-0"
								placeholder="Add to checklist (Enter to add)"
								autoFocus
								onKeyDown={(e) => {
									if (e?.key === 'Enter') {
										const updatedList = [
											...sortableChecklist,
											{ id: e.target.value, text: e.target.value },
										];
										setSortableChecklist(updatedList);
										setChecklistInput('');
										//onChange('checklist', updatedList);
									}
								}}
								onChange={(e) => setChecklistInput(e.target.value)}
								value={checklistInut}
							/>
						</div>
					</div>
				)}

				{selectedCategory?.fields.map((field) => {
					return (
						<Attributes
							field={field}
							onChange={(...props) => {
								globalOnChange(props, field, updatedForm, setForm, null, form);
							}}
							form={{
								...form,
								...updatedForm,
							}}
						/>
					);
				})}
			</>
		);
	}

	if (field?.type === 'location') {
		return (
			<AttributeRow
				name={field?.label}
				dataIndex={field?.key}
				required={field?.isRequired}
				span={field?.span ? `sm:col-span-${field?.span}` : undefined}
			>
				<div className="flex relative">
					<div className="flex-1 -mt-1">
						<SelectLocations
							value={form[field?.key]?.value || ''}
							onChange={(location, rawLocation) => {
								onChange(field?.key, location, null, rawLocation);
							}}
							locations={locations}
							isClearable={false}
						/>
					</div>
					{form[field?.key]?.value ? (
						<div className="flex-shrink flex items-center ml-3">
							<XCircleIcon
								className="w-6 h-6 cursor-pointer text-gray-500"
								onClick={() => onChange(field?.key, null)}
							/>
						</div>
					) : (
						<div className="flex-shrink flex items-center ml-3">
							<PlusCircleIcon
								className="w-6 h-6 cursor-pointer text-gray-500"
								onClick={() =>
									setAddLocationOpen({
										moduleId: field?.moduleId,
										onSubmit: (location) => {
											onChange(field?.key, location?.value);
										},
									})
								}
							/>
						</div>
					)}
				</div>
			</AttributeRow>
		);
	}

	if (field?.type === 'manufacturer') {
		const rawCompanies =
			manufacturers?.rows?.map((p) => ({
				label: p.name,
				value: p.id,
				key: p.id,
			})) || [];

		return (
			<TemplateCreateable
				name={field?.label}
				span={field?.span}
				dataIndex={field?.key}
				onChange={(...props) => {
					globalOnChange(
						[
							null,
							null,
							{
								[field?.key]: {
									value: props[1] || null,
									fieldId: field.id,
									...(props[2]?.__isNew__ ? { isNew: true } : {}),
								},
								model: null,
							},
						],
						field,
						form,
						setForm,
					);
				}}
				value={form[field?.key]?.value || ''}
				options={rawCompanies}
				disabled={field?.disabled}
				required={field?.isRequired}
			/>
		);
	}

	if (field?.type === 'model') {
		const selectedManufacturer = form['manufacturer']?.value;
		const rawModels = selectedManufacturer
			? models?.rows.map((p) => ({
					label: p.name,
					value: p.id,
					key: p.id,
				})) || []
			: [];

		return (
			<TemplateCreateable
				name={field?.label}
				span={field?.span}
				dataIndex={field?.key}
				onChange={(...props) =>
					globalOnChange(
						[
							null,
							null,
							{
								[field?.key]: {
									value: props[1] || null,
									fieldId: field.id,
									...(props[2]?.__isNew__ ? { isNew: true } : {}),
								},
							},
						],
						field,
						form,
						setForm,
					)
				}
				value={form[field?.key]?.value || ''}
				options={rawModels}
				disabled={field?.disabled || !form['manufacturer']?.value}
				required={field?.isRequired}
			/>
		);
	}

	if (field?.type === 'assets') {
		return (
			<>
				<div className="col-span-2 flex h-fit">
					<div className="flex-1">
						<TemplateReactSelect
							name={field?.label}
							dataIndex={field?.key}
							options={items?.rows
								.sort((a, b) => a.name?.localeCompare(b.name))
								.map((asset) => ({
									label: `${asset?.manufacturer?.name} ${asset?.model?.name}`,
									value: asset.id,
									asset,
								}))}
							value={form[field?.key]?.value || ''}
							onChange={(index, vendor) => {
								onChange(index, vendor);
							}}
							disabled={isLoadingItems === 'pending' || field?.disabled}
						/>
					</div>
					{form[field?.key]?.value ? (
						<div className="flex-shrink flex items-center pt-7 ml-3">
							<XCircleIcon
								className="w-6 h-6 cursor-pointer text-gray-500"
								onClick={() => onChange(field?.key, null)}
							/>
						</div>
					) : (
						<div className="flex-shrink flex items-center pt-7 ml-3">
							<PlusCircleIcon
								className="w-6 h-6 cursor-pointer text-gray-500"
								onClick={() =>
									setAddAssetOpen({
										onSuccess: (asset) => {
											onChange(field?.key, {
												label: `${asset?.manufacturer?.name || ''} ${asset?.model?.name || ''}`.trim(),
												value: asset?.id,
												asset,
											});
										},
									})
								}
							/>
						</div>
					)}
				</div>
			</>
		);
	}

	if (field?.type === 'assetsList') {
		const hideList = field?.options?.hideList;

		const selectedAssetsList = [
			...selectedAssets,
			...(field?.defaultValue ? field?.defaultValue : []),
		];

		const CustomAssetOption = ({
			isList,
			onClick,
			innerProps,
			isDisabled,
			...rest
		}) => {
			const item = rest?.data?.asset;
			return !isDisabled ? (
				<div
					{...innerProps}
					className={classNames(
						isList ? '' : 'cursor-pointer hover:bg-gray-100',
					)}
				>
					<div
						className={classNames(
							'flex items-center',
							isList ? 'py-2 px-3' : 'py-3 px-5',
						)}
					>
						<div className="ml-4 flex-1">
							<div
								className={classNames('flex items-center', isList && 'text-xs')}
							>
								{`${item?.manufacturerName || item?.manufacturer?.name} ${
									item?.modelName || item?.model?.name
								}`}
							</div>
							<div className="text-xxs text-gray-500 font-light">
								{`S/N: ${item?.serialNumber}`}
								{item?.location?.name ? ` in ${item?.location?.name}` : ''}
							</div>
						</div>
						{isList && (
							<div>
								<XMarkIcon
									className="w-8 h-8 text-gray-400 cursor-pointer py-2 px-2"
									onClick={() => {
										typeof onClick === 'function' ? onClick(item) : {};
									}}
								/>
							</div>
						)}
					</div>
				</div>
			) : null;
		};
		return (
			<>
				<div
					className={classNames(
						field?.span ? `sm:col-span-${field?.span}` : 'sm:col-span-2',
						'flex h-fit',
					)}
				>
					<div className="flex-1">
						<TemplateReactSelect
							name={field?.label}
							dataIndex={field?.key}
							required={field?.isRequired}
							span={field?.span ? `sm:col-span-${field?.span}` : undefined}
							options={items?.rows
								.sort((a, b) => a.name?.localeCompare(b.name))
								.map((asset) => ({
									label: `${asset?.manufacturer?.name} ${asset?.model?.name}`,
									value: asset.id,
									asset,
								}))}
							value={''}
							onChange={(index, asset) => {
								const updatedAssets = [...selectedAssets, asset];
								setSelectedAssets(updatedAssets);
								onChange(field?.key, null, hideList ? [asset] : updatedAssets);
							}}
							skip={
								hideList
									? form[field?.key]?.value?.map((tag) => tag.value)
									: selectedAssetsList?.map((tag) => tag.value)
							}
							disabled={isLoadingItems === 'pending' || field?.disabled}
							CustomOption={CustomAssetOption}
							filterOption={(option, input) => {
								return (
									option?.data?.asset?.serialNumber
										?.toLowerCase()
										?.indexOf(input?.toLowerCase()) > -1 ||
									option?.data?.asset?.location?.name
										?.toLowerCase()
										?.indexOf(input?.toLowerCase()) > -1 ||
									option?.data?.asset?.manufacturer?.name
										?.toLowerCase()
										?.indexOf(input?.toLowerCase()) > -1 ||
									option?.data?.asset?.model?.name
										?.toLowerCase()
										?.indexOf(input?.toLowerCase()) > -1
								);
							}}
						/>
					</div>
					<div className="flex-shrink flex items-center pt-7 ml-3">
						<PlusCircleIcon
							className="w-6 h-6 cursor-pointer text-gray-500"
							onClick={() =>
								setAddAssetOpen({
									onSuccess: (newAsset) => {
										const option = {
											label: `${newAsset?.manufacturer?.name || ''} ${newAsset?.model?.name || ''}`.trim(),
											value: newAsset?.id,
											asset: newAsset,
										};
										const updatedAssets = [...selectedAssets, option];
										setSelectedAssets(updatedAssets);
										onChange(
											field?.key,
											null,
											hideList ? [option] : updatedAssets,
										);
									},
								})
							}
						/>
					</div>
				</div>
				{hideList ? null : selectedAssetsList?.length ? (
					<div
						className={field?.span ? `sm:col-span-${field?.span}` : undefined}
					>
						{selectedAssetsList?.map((asset) => (
							<CustomAssetOption
								data={asset}
								isList={true}
								onClick={() => {
									const newValues = [
										...selectedAssetsList.filter(
											(v) => v?.value !== asset?.value,
										),
									];
									setSelectedAssets(newValues);
									onChange(
										field?.key,
										null,
										newValues?.length ? newValues : null,
									);
								}}
							/>
						))}
					</div>
				) : null}
			</>
		);
	}

	if (field?.type === 'tag') {
		return (
			<>
				<AttributeRow name="Tags" span="sm:col-span-1">
					<div className="flex items-center">
						<div className="w-full">
							<TemplateReactSelect
								options={tags?.map((tag) => ({
									label: tag?.name,
									value: tag?.id,
									color: tag?.color,
								}))}
								onChange={(index, tag) => {
									setTemp([...(temp || []), tag]);
									onChange(field?.key, null, [...(temp || []), tag]);
								}}
								skip={temp?.map((tag) => tag.value)}
								value=""
							/>
						</div>

						<div className="flex-shrink flex items-center -mt-3 pt-3 ml-3">
							<PlusCircleIcon
								className="w-6 h-6 cursor-pointer text-gray-500"
								onClick={() =>
									setAddTagOpen({
										onSubmit: (value) => {
											setTemp([...(temp || []), value]);
											onChange(field?.key, null, [...(temp || []), value]);
										},
									})
								}
							/>
						</div>
					</div>
					<div className="mt-3">
						{temp?.map((tag) => {
							return (
								<Badge
									isBeta
									className={classNames(
										TAG_COLOR_OPTIONS.find((color) => color?.key === tag?.color)
											?.value,
										'mr-1.5 mt-1.5',
									)}
									size="large"
								>
									{tag?.label}{' '}
									<XMarkIcon
										className="w-5 h-5 text-inherit-400  p-1 cursor-pointer"
										onClick={() => {
											const newValues = [
												...temp.filter((v) => v?.value !== tag?.value),
											];
											setTemp(newValues);
											onChange(field?.key, null, newValues);
										}}
									/>
								</Badge>
							);
						})}
					</div>
				</AttributeRow>
			</>
		);
	}

	return (
		<div className="text-xs text-gray-500 uppercase">{`[${field?.key}] Field not found`}</div>
	);
};

export const configurePayload = (form, fields, opts, raw) => {
	let returnForm = {};

	/*
		Takes module fields and finds default values
	*/
	const updatedFormForDefault = {
		...Object.assign(
			{},
			...fields
				?.filter((field) => field?.isActive && field?.defaultValue)
				.map((field) => ({ [field?.key]: field?.defaultValue })),
		),
		...form,
	};

	/*
		For each key in default mapping above (form), appends values to return form
	*/
	Object.keys(updatedFormForDefault).map((key) => {
		const attrId = updatedFormForDefault[key]?.id;
		const fieldId = updatedFormForDefault[key]?.fieldId;
		const field = fields.find((f) => f.id === fieldId);
		const fieldType = field?.type;

		/*
			Check if attribute field is default OR if key is generally a default attribute (vendor)
		*/
		const isDefault =
			field?.isDefault ||
			field?.key === 'category' ||
			key === 'category' ||
			key === 'assetsList' ||
			key === 'assets' ||
			key === 'vendor' ||
			key === 'tags' ||
			key === 'location';

		// Finds default value
		let value = field?.defaultValue || null;

		// Finds backup value
		let backupValue = updatedFormForDefault[key]?.backupValue;

		if (
			fieldType === 'vendor' ||
			fieldType === 'team' ||
			fieldType === 'category' ||
			//fieldType === 'location' ||
			field?.key === 'category' ||
			key === 'vendor'
		) {
			value = updatedFormForDefault[key]?.value?.value;
		} else {
			value = updatedFormForDefault[key]?.value;
		}

		if (isDefault) {
			if (fieldType === 'manufacturer') {
				returnForm[
					updatedFormForDefault[key]?.isNew
						? 'manufacturerName'
						: 'manufacturerId'
				] = value;
			} else if (fieldType === 'model') {
				returnForm[updatedFormForDefault[key]?.isNew ? 'model' : 'modelId'] =
					value;
			} else if (key === 'assetsList' || key === 'assets') {
				returnForm['assets'] = value?.map((option) => option?.value) || [];
			} else if (key === 'tags') {
				returnForm['tags'] = value?.map((option) => option?.value) || [];
			} else {
				returnForm[key] = value || null;
			}
		} else {
			const obj = { id: attrId, fieldId, value, type: fieldType, backupValue };
			if (returnForm?.customAttributes) {
				if (obj.fieldId) {
					returnForm.customAttributes.push(obj);
				}
			} else {
				if (obj.fieldId) {
					returnForm.customAttributes = [obj];
				}
			}
		}
	});

	if (raw) {
		returnForm = { ...returnForm, ...raw };
	}

	if (opts?.raw) {
		returnForm.raw = opts.raw;
	}

	if (returnForm?.images?.length) {
		const { images, ...information } = returnForm;

		return {
			information: {
				...information,
				imageFieldId: updatedFormForDefault?.images?.fieldId,
			},
			files: images?.map(({ original }) => original),
			bucket: opts?.bucket || 'general',
		};
	}

	if (returnForm?.file?.length) {
		const { file, ...information } = returnForm;

		return {
			information: {
				...information,
			},
			files: file,
			bucket: opts?.bucket || 'general',
		};
	}

	return returnForm;
};

export const globalOnChange = (
	[index, value, object, raw],
	field,
	form,
	setForm,
	isEditing,
	defaultForm = {},
) => {
	let updatedForm = {
		...form,
	};

	if (index) {
		const customAttributeId = defaultForm[index]?.id;

		updatedForm[index] = {
			value: value || null,
			fieldId: field.id,
			id: customAttributeId,
			backupValue: value?.label || raw?.name,
		};
	}

	if (object) {
		updatedForm = { ...updatedForm, ...object };
	}

	setForm(updatedForm);
};

export default Attributes;
